import type { CSSProperties, ReactNode } from "react";

// Lines that begin with a hyphen + space (e.g. "- point") are treated as
// bullet items and rendered as an intentional, styled list. Everything else
// keeps its literal line breaks (whitespace-pre-wrap). This is the single place
// thought/card body text is turned into rich blocks so every read surface —
// the detail sheets and the edition/trail readers — renders bullets the same.
const BULLET_RE = /^[ \t]*-[ \t]+(.*)$/;

// Preview surfaces (cards, map nodes) clamp to a fixed number of lines, which
// only works on a single block element — so they can't use the multi-block
// layout below. `preview` renders one whitespace-pre-wrap <p> that keeps the
// same breaks the sheet shows: leading/trailing blank lines dropped, any run of
// blank lines collapsed to the single gap a paragraph break renders as, and
// bullets marked with a glyph since there's no <ul> to carry them.
function previewText(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => {
      const m = line.match(BULLET_RE);
      return m ? `• ${m[1]}` : line;
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function ThoughtBody({
  text,
  className,
  style,
  preview = false,
}: {
  text: string;
  className?: string;
  style?: CSSProperties;
  /**
   * Render as a single clamp-compatible paragraph instead of block elements.
   * The caller keeps supplying its own `line-clamp-*` class via `className`.
   */
  preview?: boolean;
}) {
  if (preview) {
    return (
      <p className={`whitespace-pre-wrap ${className ?? ""}`} style={style}>
        {previewText(text)}
      </p>
    );
  }

  const lines = text.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let bullets: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="whitespace-pre-wrap">
        {para.join("\n")}
      </p>
    );
    para = [];
  };

  const flushBullets = () => {
    if (bullets.length === 0) return;
    blocks.push(
      <ul key={`u-${blocks.length}`} className="list-none space-y-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2.5">
            <span
              aria-hidden
              className="mt-[0.62em] h-[5px] w-[5px] shrink-0 rounded-full bg-accent/60"
            />
            <span className="min-w-0 flex-1 whitespace-pre-wrap">{b}</span>
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };

  for (const line of lines) {
    const m = line.match(BULLET_RE);
    if (m) {
      flushPara();
      bullets.push(m[1]);
    } else if (line.trim() === "") {
      // Blank line ends the current block and creates a paragraph break.
      flushBullets();
      flushPara();
    } else {
      flushBullets();
      para.push(line);
    }
  }
  flushBullets();
  flushPara();

  return (
    <div className={`space-y-2.5 ${className ?? ""}`} style={style}>
      {blocks}
    </div>
  );
}
